import React, { useMemo } from 'react';
import { buildCharacterSpellbookViewModel } from '../utils/CharacterSpellbookPresentation';
import './CharacterSpellbookPanel.css';

const signed = (value) => value == null ? '—' : `${value >= 0 ? '+' : ''}${value}`;

const CharacterSpellbookPanel = ({ subject = null, spellData = {}, className = '' }) => {
  const model = useMemo(
    () => buildCharacterSpellbookViewModel(spellData, subject),
    [spellData, subject]
  );

  return (
    <section className={`character-spellbook-panel ${className}`.trim()} aria-label="Spellbook reference">
      <header className="character-spellbook-panel__header">
        <div>
          <p>Spellcasting Reference</p>
          <h2>Spellbook</h2>
        </div>
        <span>{model.spellCount} recorded spell{model.spellCount === 1 ? '' : 's'}</span>
      </header>

      <div className="character-spellbook-panel__stats" aria-label="Spellcasting statistics">
        <div><span>Ability</span><strong>{model.ability || '—'}</strong><small>{signed(model.modifier)} modifier</small></div>
        <div><span>Save DC</span><strong>{model.saveDC ?? '—'}</strong><small>Spell saves</small></div>
        <div><span>Attack</span><strong>{signed(model.attackBonus)}</strong><small>Spell attacks</small></div>
        <div className="character-spellbook-panel__slots">
          <span>Available Slots</span>
          {model.slots.length > 0 ? (
            <div>{model.slots.map((slot) => (
              <span key={slot.level}><b>L{slot.level}</b> {slot.remaining}/{slot.total}<small>{slot.used} used</small></span>
            ))}</div>
          ) : <strong>—</strong>}
        </div>
      </div>

      {model.groups.length > 1 && (
        <nav className="character-spellbook-panel__nav" aria-label="Spell levels">
          {model.groups.map((group) => <a key={group.level} href={`#spell-level-${group.level}`}>{group.title} · {group.spells.length}</a>)}
        </nav>
      )}

      {model.groups.length === 0 ? (
        <p className="character-spellbook-panel__empty">No spells recorded.</p>
      ) : (
        <div className="character-spellbook-panel__groups">
          {model.groups.map((group) => (
            <details key={group.level} id={`spell-level-${group.level}`} open className="character-spellbook-panel__group">
              <summary><span>{group.title}</span><b>{group.spells.length}</b></summary>
              <div className="character-spellbook-panel__grid">
                {group.spells.map((spell) => (
                  <article key={spell.key} className="character-spellbook-card">
                    <header>
                      <h3>{spell.name}</h3>
                      <div>{spell.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                    </header>
                    {spell.acquisition && <p className="character-spellbook-card__source">{spell.acquisition}</p>}
                    {spell.mechanics.length > 0 && (
                      <dl>{spell.mechanics.map((mechanic) => (
                        <div key={`${mechanic.label}-${mechanic.value}`}><dt>{mechanic.label}</dt><dd>{mechanic.value}</dd></div>
                      ))}</dl>
                    )}
                    {(spell.description || spell.material || spell.modification) && (
                      <details className="character-spellbook-card__rules" open={spell.description.length <= 220}>
                        <summary>Rules & notes</summary>
                        {spell.material && <p><strong>Material:</strong> {spell.material}</p>}
                        {spell.modification && <p><strong>Character effect:</strong> {spell.modification}</p>}
                        {spell.description && <p>{spell.description}</p>}
                      </details>
                    )}
                  </article>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
};

export default CharacterSpellbookPanel;
